/**
 * portfolio-fx.js — v3 "Chromatic+" interaction engine for jdenandro.
 *
 * Site-wide magic, all monochrome:
 *  1. Toast system + window.PFX command API (used by the Command Deck).
 *  2. Programs: invert (world flip), gravity (page falls, recovers),
 *     matrix (ink rain), smoke/boring toggles — persisted prefs.
 *  3. Constellation: the engineering grid comes alive — nodes glow near
 *     the cursor, clicks/taps send a radial shockwave through the grid.
 *  4. Scroll-velocity skew: the page leans with your momentum.
 *  5. Marquees respond to scroll speed and direction.
 *  6. Heading decode + variable-weight "ink repel" type on hover.
 *  7. Smoke cursor (difference blend) + click ink rings.
 *  8. Spring-physics magnetic buttons, specular card tilt.
 *  9. Ink-wipe view transitions (coords handed to the next page).
 * 10. Easter eggs: Konami code, typed words, idle nudge.
 *
 * Everything gated behind prefers-reduced-motion and the "boring" pref.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const lerp = (a, b, t) => a + (b - a) * t;

  /* =================================================================
     0. Prefs
     ================================================================= */
  const PREF_KEY = 'dna_fx';
  const loadPrefs = () => {
    try {
      return Object.assign({ boring: false, smoke: true, invert: false }, JSON.parse(localStorage.getItem(PREF_KEY) || '{}'));
    } catch (error) {
      return { boring: false, smoke: true, invert: false };
    }
  };
  const prefs = loadPrefs();
  const savePrefs = () => {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify(prefs));
    } catch (error) {
      /* private mode — prefs just won't stick */
    }
  };

  /* =================================================================
     1. Toasts — terminal lines, bottom-left
     ================================================================= */
  let toastHost = null;
  const toast = (msg, ms = 3600) => {
    if (!toastHost) {
      toastHost = document.createElement('div');
      toastHost.className = 'pfx-toasts';
      toastHost.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastHost);
    }
    const line = document.createElement('div');
    line.className = 'pfx-toast';
    line.textContent = `> ${msg}`;
    toastHost.appendChild(line);
    while (toastHost.children.length > 3) toastHost.firstChild.remove();
    window.requestAnimationFrame(() => line.classList.add('pfx-toast--in'));
    window.setTimeout(() => {
      line.classList.remove('pfx-toast--in');
      window.setTimeout(() => line.remove(), 350);
    }, ms);
  };

  /* =================================================================
     2. Programs (PFX API)
     ================================================================= */
  const motionAllowed = () => !reducedMotion && !prefs.boring;

  const applyInvert = () => {
    document.documentElement.classList.toggle('pfx-inverted', Boolean(prefs.invert));
  };
  applyInvert();

  const invert = () => {
    prefs.invert = !prefs.invert;
    savePrefs();
    applyInvert();
    toast(prefs.invert ? 'WORLD INVERTED — welcome to the dark room' : 'WORLD RESTORED — lights on');
  };

  /* --- gravity: the visible page falls, bounces, recovers --- */
  let gravityActive = false;
  const gravity = () => {
    if (!motionAllowed()) return toast('MOTION IS OFF ON THIS DEVICE.');
    if (gravityActive) return toast('GRAVITY ALREADY ENGAGED.');
    const vh = window.innerHeight;
    const candidates = document.querySelectorAll(
      '#MainContent :is(h1, h2, h3, h4, p, li, img, figure, .button, .dna-band__word, a.link)'
    );
    const bodies = [];
    for (const el of candidates) {
      if (bodies.length >= 140) break;
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh || rect.width === 0 || rect.height === 0) continue;
      if (el.closest('[data-pfx-falling]')) continue;
      el.setAttribute('data-pfx-falling', '');
      bodies.push({
        el,
        prev: el.style.transform || '',
        x: 0,
        y: 0,
        vx: (Math.random() - 0.5) * 60,
        vy: -Math.random() * 140 - 40,
        rot: 0,
        vr: (Math.random() - 0.5) * 130,
        floor: Math.max(vh - rect.bottom, 0),
      });
    }
    if (!bodies.length) return toast('NOTHING TO DROP HERE.');

    gravityActive = true;
    toast('GRAVITY: ENGAGED');
    const G = 2600;
    let last = performance.now();
    const started = last;

    const step = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      for (const b of bodies) {
        b.vy += G * dt;
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        b.rot += b.vr * dt;
        if (b.y >= b.floor) {
          b.y = b.floor;
          if (Math.abs(b.vy) > 60) {
            b.vy = -b.vy * 0.42;
            b.vr *= 0.6;
            b.vx *= 0.8;
          } else {
            b.vy = 0;
            b.vx *= 0.86;
            b.vr *= 0.86;
          }
        }
        b.el.style.transform = `translate(${b.x.toFixed(1)}px, ${b.y.toFixed(1)}px) rotate(${b.rot.toFixed(2)}deg)`;
      }
      if (now - started < 3400) {
        window.requestAnimationFrame(step);
      } else {
        for (const b of bodies) {
          b.el.style.transition = 'transform 0.9s cubic-bezier(0.22, 1, 0.36, 1)';
          b.el.style.transform = b.prev;
        }
        window.setTimeout(() => {
          for (const b of bodies) {
            b.el.style.transition = '';
            if (!b.prev) b.el.style.removeProperty('transform');
            b.el.removeAttribute('data-pfx-falling');
          }
          gravityActive = false;
          toast('SYSTEM RESTORED. nothing broke. probably.');
        }, 950);
      }
    };
    window.requestAnimationFrame(step);
  };

  /* --- matrix: 8 seconds of ink rain --- */
  let matrixActive = false;
  const matrix = () => {
    if (!motionAllowed()) return toast('MOTION IS OFF ON THIS DEVICE.');
    if (matrixActive) return;
    matrixActive = true;
    const canvas = document.createElement('canvas');
    canvas.className = 'pfx-matrix';
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    // Draw paper-white + ink-black always: the root invert filter flips
    // the whole canvas in dark mode, so branching would double-flip.
    const paper = 'rgba(255,255,255,0.16)';
    const ink = '#0a0a0a';
    const GLYPHS = 'アイウエオカキクケコサシスセソ0123456789<>/{}[]=+*#$&';
    const colWidth = 16;
    const cols = Math.ceil(canvas.width / colWidth);
    const drops = Array.from({ length: cols }, () => Math.random() * -40);
    const start = performance.now();

    const step = (now) => {
      const t = now - start;
      ctx.fillStyle = paper;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = ink;
      ctx.font = '13px "JetBrains Mono", monospace';
      for (let i = 0; i < cols; i++) {
        const ch = GLYPHS[(Math.random() * GLYPHS.length) | 0];
        ctx.fillText(ch, i * colWidth, drops[i] * 15);
        if (drops[i] * 15 > canvas.height && Math.random() > 0.975) drops[i] = 0;
        drops[i] += 0.72;
      }
      if (t < 7200) {
        window.requestAnimationFrame(step);
      } else {
        canvas.classList.add('pfx-matrix--out');
        window.setTimeout(() => {
          canvas.remove();
          matrixActive = false;
        }, 700);
      }
    };
    toast('WAKE UP. THE GRID HAS YOU.');
    window.requestAnimationFrame(step);
  };

  const toggleBoring = () => {
    prefs.boring = !prefs.boring;
    savePrefs();
    toast(prefs.boring ? 'MAGIC: OFF. rebooting plain…' : 'MAGIC: ON. rebooting wild…');
    window.setTimeout(() => window.location.reload(), 900);
  };

  let smokeSetEnabled = null; // wired by the smoke system below
  const toggleSmoke = () => {
    prefs.smoke = !prefs.smoke;
    savePrefs();
    if (smokeSetEnabled) smokeSetEnabled(prefs.smoke);
    else if (prefs.smoke) return window.setTimeout(() => window.location.reload(), 700);
    toast(prefs.smoke ? 'VAPOR: ON' : 'VAPOR: OFF');
  };

  /* --- session telemetry (fuel for the `stats` program) --- */
  const session = { start: Date.now(), clicks: 0, keys: 0, scrolled: 0, lastY: window.scrollY };
  document.addEventListener('pointerdown', () => (session.clicks += 1), { capture: true, passive: true });
  document.addEventListener('keydown', () => (session.keys += 1), { capture: true, passive: true });
  window.addEventListener(
    'scroll',
    () => {
      session.scrolled += Math.abs(window.scrollY - session.lastY);
      session.lastY = window.scrollY;
    },
    { passive: true }
  );

  const stats = () => {
    const secs = Math.floor((Date.now() - session.start) / 1000);
    const mins = Math.floor(secs / 60);
    const meters = (session.scrolled / 3780).toFixed(1); // ~96dpi → metres
    toast(`SESSION: ${mins}m${String(secs % 60).padStart(2, '0')}s · ${session.clicks} clicks · ${session.keys} keys`);
    window.setTimeout(() => toast(`SCROLLED ${Math.round(session.scrolled).toLocaleString()}px (~${meters}m). cardio.`), 1400);
  };

  /* --- roll & flip: the page performs --- */
  const roll = () => {
    if (!motionAllowed()) return toast('MOTION IS OFF ON THIS DEVICE.');
    toast('DO A BARREL ROLL');
    document.body.animate(
      [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
      { duration: 1300, easing: 'cubic-bezier(0.55, -0.2, 0.3, 1.2)' }
    );
  };

  const flip = () => {
    if (!motionAllowed()) return toast('MOTION IS OFF ON THIS DEVICE.');
    toast('AUSTRALIA MODE — 4 SECONDS');
    document.body.animate(
      [
        { transform: 'rotate(0deg)' },
        { transform: 'rotate(180deg)', offset: 0.12 },
        { transform: 'rotate(180deg)', offset: 0.88 },
        { transform: 'rotate(360deg)' },
      ],
      { duration: 4200, easing: 'ease-in-out' }
    );
  };

  /* --- zen: a breathing exercise, esc/tap to leave --- */
  let zenEl = null;
  const zen = () => {
    if (zenEl) return;
    zenEl = document.createElement('div');
    zenEl.className = 'pfx-zen';
    zenEl.innerHTML =
      '<div class="pfx-zen__ring" aria-hidden="true"></div>' +
      '<p class="pfx-zen__word" aria-live="polite">breathe in</p>' +
      '<p class="pfx-zen__exit">tap anywhere / esc to return to the grind</p>';
    document.body.appendChild(zenEl);
    const word = zenEl.querySelector('.pfx-zen__word');
    let phase = 0;
    const WORDS = ['breathe in', 'hold', 'breathe out', 'hold'];
    const cycle = window.setInterval(() => {
      phase = (phase + 1) % 4;
      word.textContent = WORDS[phase];
    }, 4000);
    const leave = () => {
      window.clearInterval(cycle);
      document.removeEventListener('keydown', onKey);
      zenEl.classList.add('pfx-zen--out');
      window.setTimeout(() => {
        zenEl?.remove();
        zenEl = null;
      }, 500);
      toast('NAMASTE. BACK TO WORK.');
    };
    const onKey = (event) => {
      if (event.key === 'Escape') leave();
    };
    zenEl.addEventListener('pointerdown', leave);
    document.addEventListener('keydown', onKey);
    window.requestAnimationFrame(() => zenEl?.classList.add('pfx-zen--in'));
  };

  /* --- hack: dramatic fake terminal, harmless by design --- */
  let hackActive = false;
  const hack = () => {
    if (hackActive) return;
    hackActive = true;
    const panel = document.createElement('div');
    panel.className = 'pfx-hack';
    panel.innerHTML = '<pre class="pfx-hack__log"></pre>';
    document.body.appendChild(panel);
    const log = panel.querySelector('.pfx-hack__log');
    const LINES = [
      'init exploit.sh --target jdenandro.com',
      'scanning ports .... 80 443 8080',
      'bypassing firewall ......... ok',
      'grabbing credentials ....... ok',
      'decrypting vault ........... ok',
      'extracting secrets ......... ok',
      '> secret_1: he uses claude code for everything',
      '> secret_2: the "AI magic" is mostly good taste',
      '> secret_3: coffee intake: unsustainable',
      'ACCESS GRANTED.',
      '…just kidding. everything here was public anyway.',
    ];
    let i = 0;
    const type = () => {
      if (i < LINES.length) {
        log.textContent += `$ ${LINES[i]}\n`;
        log.scrollTop = log.scrollHeight;
        i += 1;
        window.setTimeout(type, i > 5 ? 260 : 340);
      } else {
        window.setTimeout(() => {
          panel.classList.add('pfx-hack--out');
          window.setTimeout(() => {
            panel.remove();
            hackActive = false;
          }, 500);
        }, 2200);
      }
    };
    window.requestAnimationFrame(() => panel.classList.add('pfx-hack--in'));
    window.setTimeout(type, 350);
  };

  /* --- bali: studio local time --- */
  const bali = () => {
    let time = '';
    try {
      time = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Makassar', hour: '2-digit', minute: '2-digit' }).format(new Date());
    } catch (error) {
      time = 'GMT+8';
    }
    toast(`BALI STUDIO — ${time} · GMT+8 · ~28°C · surf status: probably good`);
  };

  /* --- dvd: the screensaver we all watched --- */
  let dvdActive = false;
  const dvd = () => {
    if (dvdActive) return;
    if (!motionAllowed()) return toast('MOTION IS OFF ON THIS DEVICE.');
    dvdActive = true;
    const screen = document.createElement('div');
    screen.className = 'pfx-dvd';
    const box = document.createElement('div');
    box.className = 'pfx-dvd__logo';
    box.textContent = 'DENANDRO';
    screen.appendChild(box);
    document.body.appendChild(screen);
    let x = 60;
    let y = 80;
    let vx = 2.6;
    let vy = 2.1;
    let solid = false;
    let raf = null;
    const started = performance.now();
    const step = (now) => {
      const bw = box.offsetWidth;
      const bh = box.offsetHeight;
      x += vx;
      y += vy;
      let hit = false;
      if (x <= 0 || x + bw >= window.innerWidth) {
        vx = -vx;
        x = clamp(x, 0, window.innerWidth - bw);
        hit = true;
      }
      if (y <= 0 || y + bh >= window.innerHeight) {
        vy = -vy;
        y = clamp(y, 0, window.innerHeight - bh);
        hit = true;
      }
      if (hit) {
        solid = !solid;
        box.classList.toggle('pfx-dvd__logo--solid', solid);
      }
      box.style.transform = `translate(${x}px, ${y}px)`;
      if (now - started < 45000) raf = window.requestAnimationFrame(step);
      else leave();
    };
    const leave = () => {
      if (raf) window.cancelAnimationFrame(raf);
      document.removeEventListener('keydown', leave);
      screen.remove();
      dvdActive = false;
      toast('IT NEVER HITS THE CORNER. THAT IS THE LESSON.');
    };
    screen.addEventListener('pointerdown', leave);
    document.addEventListener('keydown', leave, { once: true });
    raf = window.requestAnimationFrame(step);
  };

  /* --- bot: the DENANDRO.EXE runner sprints across the page --- */
  let botActive = false;
  const bot = () => {
    if (botActive) return;
    if (!motionAllowed()) return toast('MOTION IS OFF ON THIS DEVICE.');
    botActive = true;
    const runner = document.createElement('div');
    runner.className = 'pfx-bot';
    runner.innerHTML = '<span class="pfx-bot__eye"></span><span class="pfx-bot__leg pfx-bot__leg--a"></span><span class="pfx-bot__leg pfx-bot__leg--b"></span>';
    document.body.appendChild(runner);
    window.setTimeout(() => {
      runner.remove();
      botActive = false;
      toast('DENANDRO.EXE PASSED BY. IT HAS SOMEWHERE TO BE.');
    }, 4300);
  };

  window.PFX = {
    toast,
    invert,
    gravity,
    matrix,
    toggleBoring,
    toggleSmoke,
    stats,
    roll,
    flip,
    zen,
    hack,
    bali,
    dvd,
    bot,
    prefs,
  };

  /* =================================================================
     3. View-transition ink wipe — hand coords to the next page
     ================================================================= */
  document.addEventListener(
    'pointerdown',
    (event) => {
      try {
        sessionStorage.setItem(
          'pfx_vt',
          JSON.stringify({ x: event.clientX, y: event.clientY, t: Date.now() })
        );
      } catch (error) {
        /* fine — transition falls back to a crossfade */
      }
    },
    { capture: true, passive: true }
  );

  /* =================================================================
     4. Scroll progress hairline (always on — honest UI)
     ================================================================= */
  const progress = document.querySelector('.pfx-progress');
  if (progress) {
    let ticking = false;
    const update = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      progress.style.setProperty('--pfx-scroll', max > 0 ? (doc.scrollTop / max).toFixed(4) : 0);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  if (!motionAllowed()) return;

  /* =================================================================
     5. Heading decode + variable-weight "ink repel" type
     ================================================================= */
  const DECODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz/\\|·—';
  const randomChar = () => DECODE_CHARS[(Math.random() * DECODE_CHARS.length) | 0];

  /* Split display headings into per-character spans so the cursor can
     push ink around. Skips composed headings (nested markup) and the
     hero/band, which run their own shows. */
  const splitChars = (heading) => {
    if (heading.childElementCount > 0) return false;
    const text = heading.textContent;
    if (!text || text.trim().length === 0 || text.length > 48) return false;
    if (heading.closest('.dna-hero, .dna-band, .dna-arcade')) return false;
    const frag = document.createDocumentFragment();
    for (const ch of text) {
      if (ch === ' ' || ch === '\n' || ch === '\t') {
        frag.appendChild(document.createTextNode(' '));
      } else {
        const span = document.createElement('span');
        span.className = 'pfx-gt-c';
        span.textContent = ch;
        frag.appendChild(span);
      }
    }
    heading.textContent = '';
    heading.appendChild(frag);
    heading.classList.add('pfx-gt');
    return true;
  };

  const decodeChars = (heading) => {
    const chars = heading.querySelectorAll('.pfx-gt-c');
    const start = performance.now();
    const plan = Array.from(chars, (span, i) => ({
      span,
      original: span.textContent,
      settle: i * 26 + 220 + Math.random() * 120,
    }));
    const frame = (now) => {
      const t = now - start;
      let live = false;
      for (const p of plan) {
        if (t < p.settle) {
          p.span.textContent = randomChar();
          live = true;
        } else if (p.span.textContent !== p.original) {
          p.span.textContent = p.original;
        }
      }
      if (live) window.requestAnimationFrame(frame);
    };
    window.requestAnimationFrame(frame);
  };

  const decodeLeaves = (el) => {
    const leaves = [];
    const walk = (node) => {
      if (node.childElementCount === 0) {
        if (node.textContent.trim()) leaves.push(node);
        return;
      }
      Array.from(node.children).forEach(walk);
    };
    walk(el);
    leaves.forEach((leaf) => {
      const original = leaf.textContent;
      if (original.length > 140) return;
      leaf.classList.add('pfx-decode');
      const start = performance.now();
      const duration = 700 + Math.min(original.length * 14, 500);
      const frame = (now) => {
        const t = Math.min((now - start) / duration, 1);
        const settled = Math.floor(t * original.length);
        let out = original.slice(0, settled);
        for (let i = settled; i < original.length; i++) {
          const ch = original[i];
          out += ch === ' ' ? ' ' : randomChar();
        }
        leaf.textContent = out;
        if (t < 1) window.requestAnimationFrame(frame);
        else leaf.textContent = original;
      };
      window.requestAnimationFrame(frame);
    });
  };

  /* Cursor proximity bends the type: near letters go thin and lift,
     like ink pressed away. Needs the variable font axis (300–700). */
  const bindInkRepel = (heading) => {
    if (!finePointer) return;
    let raf = null;
    let px = 0;
    let py = 0;
    let inside = false;
    const chars = Array.from(heading.querySelectorAll('.pfx-gt-c'), (span) => ({ span, w: 600, y: 0 }));
    if (!chars.length) return;

    const loop = () => {
      let settled = true;
      const rects = chars.map((c) => c.span.getBoundingClientRect());
      chars.forEach((c, i) => {
        const rect = rects[i];
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dist = Math.hypot(px - cx, py - cy);
        const t = inside ? clamp(1 - dist / 150, 0, 1) : 0;
        const targetW = 600 - t * 280;
        const targetY = -6 * t;
        c.w = lerp(c.w, targetW, 0.22);
        c.y = lerp(c.y, targetY, 0.22);
        if (Math.abs(c.w - targetW) > 0.5 || Math.abs(c.y - targetY) > 0.1) settled = false;
        c.span.style.fontWeight = c.w.toFixed(0);
        c.span.style.transform = c.y < -0.15 ? `translateY(${c.y.toFixed(2)}px)` : '';
      });
      raf = !inside && settled ? null : window.requestAnimationFrame(loop);
    };

    heading.addEventListener('pointerenter', () => {
      inside = true;
      if (!raf) raf = window.requestAnimationFrame(loop);
    });
    heading.addEventListener('pointermove', (event) => {
      px = event.clientX;
      py = event.clientY;
    });
    heading.addEventListener('pointerleave', () => {
      inside = false;
    });
  };

  const decode = (heading) => {
    if (splitChars(heading)) {
      decodeChars(heading);
      bindInkRepel(heading);
    } else {
      decodeLeaves(heading);
    }
  };

  /* =================================================================
     6. Scroll reveal
     ================================================================= */
  const revealables = [];
  document.querySelectorAll('#MainContent > .shopify-section').forEach((section) => {
    section.setAttribute('data-pfx-reveal', '');
    revealables.push(section);
  });
  document.querySelectorAll('.product-card, .resource-card, .collection-card').forEach((card, i) => {
    card.setAttribute('data-pfx-reveal', '');
    card.style.setProperty('--pfx-delay', `${Math.min((i % 8) * 70, 490)}ms`);
    revealables.push(card);
  });

  const reveal = (el) => {
    el.classList.add('pfx-in');
    el.querySelectorAll('h1, h2, h3').forEach((heading) => {
      if (!heading.dataset.pfxDecoded) {
        heading.dataset.pfxDecoded = 'true';
        if (heading.matches('h1, h2')) decode(heading);
        else decodeLeaves(heading);
      }
    });
  };

  if (revealables.length) {
    document.body.classList.add('pfx-ready');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    revealables.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) reveal(el);
      else io.observe(el);
    });
  }

  /* =================================================================
     7. Scroll velocity — page lean + marquee response
     ================================================================= */
  const main = document.getElementById('MainContent');
  const marqueeTracks = document.querySelectorAll('.dna-band__track, .rlc__track');
  let velRaf = null;
  let lastScrollY = window.scrollY;
  let vel = 0;

  const velLoop = () => {
    const y = window.scrollY;
    vel = lerp(vel, (y - lastScrollY) * 60, 0.12); // ~px per second
    lastScrollY = y;

    if (main) {
      const skew = clamp(vel * 0.0006, -1.3, 1.3);
      if (Math.abs(skew) > 0.03) {
        main.style.transform = `skewY(${skew.toFixed(3)}deg)`;
        main.style.willChange = 'transform';
      } else {
        main.style.transform = '';
        main.style.willChange = '';
      }
    }

    marqueeTracks.forEach((track) => {
      const anims = track.getAnimations ? track.getAnimations() : [];
      for (const anim of anims) {
        const magnitude = clamp(1 + Math.abs(vel) / 1400, 1, 3.4);
        const rate = vel < -160 ? -magnitude : magnitude;
        try {
          if (anim.updatePlaybackRate) anim.updatePlaybackRate(rate);
          else anim.playbackRate = rate;
        } catch (error) {
          /* older engines: leave the marquee at natural speed */
        }
      }
    });

    if (Math.abs(vel) > 1 || window.scrollY !== lastScrollY) {
      velRaf = window.requestAnimationFrame(velLoop);
    } else {
      if (main) {
        main.style.transform = '';
        main.style.willChange = '';
      }
      velRaf = null;
    }
  };

  window.addEventListener(
    'scroll',
    () => {
      if (!velRaf) {
        lastScrollY = window.scrollY;
        velRaf = window.requestAnimationFrame(velLoop);
      }
    },
    { passive: true }
  );

  /* =================================================================
     8. Constellation — the grid is alive
     ================================================================= */
  const ambient = document.querySelector('.pfx-ambient');
  if (ambient) {
    const stars = document.createElement('canvas');
    stars.className = 'pfx-stars';
    ambient.appendChild(stars);
    const sctx = stars.getContext('2d');
    const GRID = 56;
    let sw = 0;
    let sh = 0;
    const sizeStars = () => {
      sw = stars.width = window.innerWidth;
      sh = stars.height = window.innerHeight;
    };
    sizeStars();
    window.addEventListener('resize', sizeStars);

    let cx = -1000;
    let cy = -1000;
    let lastMove = 0;
    const waves = [];
    let starsRaf = null;

    const starsLoop = (now) => {
      sctx.clearRect(0, 0, sw, sh);
      const color = '0,0,0'; // root invert filter recolors this in dark mode
      const hoverLive = finePointer && now - lastMove < 2600;

      /* survey the wave set once */
      for (let i = waves.length - 1; i >= 0; i--) {
        waves[i].r += 13;
        waves[i].life -= 0.012;
        if (waves[i].life <= 0) waves.splice(i, 1);
      }

      const x0 = Math.max(0, Math.floor((cx - 190) / GRID));
      const x1 = Math.min(Math.ceil(sw / GRID), Math.ceil((cx + 190) / GRID));
      const y0 = Math.max(0, Math.floor((cy - 190) / GRID));
      const y1 = Math.min(Math.ceil(sh / GRID), Math.ceil((cy + 190) / GRID));

      /* hover field: nodes swell near the cursor, web lines reach it */
      if (hoverLive) {
        for (let gx = x0; gx <= x1; gx++) {
          for (let gy = y0; gy <= y1; gy++) {
            const nx = gx * GRID;
            const ny = gy * GRID;
            const dist = Math.hypot(cx - nx, cy - ny);
            const t = clamp(1 - dist / 180, 0, 1);
            if (t <= 0.02) continue;
            const a = t * t * 0.55;
            sctx.fillStyle = `rgba(${color},${a.toFixed(3)})`;
            sctx.beginPath();
            sctx.arc(nx, ny, 1.1 + t * 2.1, 0, Math.PI * 2);
            sctx.fill();
            if (t > 0.45) {
              sctx.strokeStyle = `rgba(${color},${(t * 0.14).toFixed(3)})`;
              sctx.lineWidth = 1;
              sctx.beginPath();
              sctx.moveTo(nx, ny);
              sctx.lineTo(cx, cy);
              sctx.stroke();
            }
          }
        }
      }

      /* shockwaves: a ring travels the whole grid */
      if (waves.length) {
        const gw = Math.ceil(sw / GRID);
        const gh = Math.ceil(sh / GRID);
        for (const wave of waves) {
          const band = 64;
          const gx0 = Math.max(0, Math.floor((wave.x - wave.r - band) / GRID));
          const gx1 = Math.min(gw, Math.ceil((wave.x + wave.r + band) / GRID));
          const gy0 = Math.max(0, Math.floor((wave.y - wave.r - band) / GRID));
          const gy1 = Math.min(gh, Math.ceil((wave.y + wave.r + band) / GRID));
          for (let gx = gx0; gx <= gx1; gx++) {
            for (let gy = gy0; gy <= gy1; gy++) {
              const nx = gx * GRID;
              const ny = gy * GRID;
              const d = Math.abs(Math.hypot(nx - wave.x, ny - wave.y) - wave.r);
              if (d > band) continue;
              const t = (1 - d / band) * wave.life;
              if (t <= 0.03) continue;
              sctx.fillStyle = `rgba(${color},${(t * 0.5).toFixed(3)})`;
              sctx.beginPath();
              sctx.arc(nx, ny, 1 + t * 2.4, 0, Math.PI * 2);
              sctx.fill();
            }
          }
        }
      }

      starsRaf = hoverLive || waves.length ? window.requestAnimationFrame(starsLoop) : null;
      if (!starsRaf) sctx.clearRect(0, 0, sw, sh);
    };

    const wakeStars = () => {
      if (!starsRaf) starsRaf = window.requestAnimationFrame(starsLoop);
    };

    if (finePointer) {
      document.addEventListener(
        'pointermove',
        (event) => {
          cx = event.clientX;
          cy = event.clientY;
          lastMove = performance.now();
          wakeStars();
        },
        { passive: true }
      );
    }

    document.addEventListener(
      'pointerdown',
      (event) => {
        waves.push({ x: event.clientX, y: event.clientY, r: 8, life: 1 });
        if (waves.length > 4) waves.shift();
        wakeStars();
      },
      { passive: true }
    );
  }

  /* =================================================================
     9. Easter eggs — Konami, typed words, idle nudge
     ================================================================= */
  const KONAMI = ['arrowup', 'arrowup', 'arrowdown', 'arrowdown', 'arrowleft', 'arrowright', 'arrowleft', 'arrowright', 'b', 'a'];
  let konamiAt = 0;
  let typed = '';

  document.addEventListener('keydown', (event) => {
    const target = event.target;
    if (target instanceof HTMLElement && (target.isContentEditable || /^(input|textarea|select)$/i.test(target.tagName))) {
      return;
    }
    const key = event.key.toLowerCase();

    konamiAt = key === KONAMI[konamiAt] ? konamiAt + 1 : key === KONAMI[0] ? 1 : 0;
    if (konamiAt === KONAMI.length) {
      konamiAt = 0;
      try {
        sessionStorage.setItem('dna_cheat', '1');
      } catch (error) {
        /* the arcade just won't get the free shield */
      }
      toast('CHEAT ACCEPTED — booting DENANDRO.EXE with +1 shield');
      window.setTimeout(() => {
        window.location.href = '/pages/play';
      }, 1100);
    }

    if (/^[a-z]$/.test(key)) {
      typed = (typed + key).slice(-12);
      if (typed.endsWith('claude')) {
        typed = '';
        toast('hello. i helped wire this place. — claude');
      } else if (typed.endsWith('ship')) {
        typed = '';
        toast('SHIPPED. that is the whole philosophy.');
      }
    }
  });

  let idleTimer = null;
  const armIdle = () => {
    window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => {
      try {
        if (sessionStorage.getItem('dna_idle_nudge')) return;
        sessionStorage.setItem('dna_idle_nudge', '1');
      } catch (error) {
        /* fall through — worst case the nudge repeats next session */
      }
      toast('still there? ⌘K opens the command deck.');
    }, 60000);
  };
  ['pointermove', 'keydown', 'scroll', 'touchstart'].forEach((evt) =>
    window.addEventListener(evt, armIdle, { passive: true })
  );
  armIdle();

  if (!finePointer) return;

  /* =================================================================
     10. Smoke cursor + click ink rings (difference blend)
     ================================================================= */
  const dot = document.createElement('div');
  dot.className = 'pfx-cursor-dot';
  const smoke = document.createElement('canvas');
  smoke.className = 'pfx-smoke';
  document.body.append(smoke, dot);

  const SCALE = 0.5;
  const ctx = smoke.getContext('2d');
  const sizeCanvas = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    smoke.width = Math.ceil(w * SCALE);
    smoke.height = Math.ceil(h * SCALE);
    smoke.style.width = w + 'px';
    smoke.style.height = h + 'px';
  };
  sizeCanvas();
  window.addEventListener('resize', sizeCanvas);

  const particles = [];
  const rings = [];
  const MAX_PARTICLES = 260;
  let mx = -100;
  let my = -100;
  let lastX = -100;
  let lastY = -100;
  let hoverBoost = 1;
  let smokeRaf = null;
  let tick = 0;
  let smokeEnabled = prefs.smoke !== false;

  smokeSetEnabled = (on) => {
    smokeEnabled = on;
    smoke.style.display = on ? '' : 'none';
  };
  if (!smokeEnabled) smoke.style.display = 'none';

  const INTERACTIVE = 'a, button, [role="button"], input, textarea, select, summary, label';

  const flowAngle = (x, y, t) =>
    Math.sin(x * 0.006 + t * 0.011) * 1.9 +
    Math.cos(y * 0.005 - t * 0.008) * 1.9 +
    Math.sin((x + y) * 0.003 + t * 0.005);

  const spawn = (x, y, vx, vy) => {
    if (!smokeEnabled) return;
    if (particles.length >= MAX_PARTICLES) particles.shift();
    const boost = hoverBoost;
    particles.push({
      x: x + (Math.random() - 0.5) * 14,
      y: y + (Math.random() - 0.5) * 14,
      vx: vx * (0.055 + Math.random() * 0.05) + (Math.random() - 0.5) * 0.55,
      vy: vy * (0.055 + Math.random() * 0.05) + (Math.random() - 0.5) * 0.55,
      r: (16 + Math.random() * 26) * boost,
      grow: 1.05 + Math.random() * 0.7,
      swirl: 0.04 + Math.random() * 0.08,
      life: 1,
      decay: 0.006 + Math.random() * 0.0055,
      seed: Math.random() * 1000,
    });
  };

  const emitPath = (x0, y0, x1, y1) => {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const dist = Math.hypot(dx, dy);
    const steps = Math.max(1, Math.min(Math.floor(dist / 9), 10));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      spawn(x0 + dx * t, y0 + dy * t, dx, dy);
    }
  };

  const smokeLoop = () => {
    tick++;
    ctx.clearRect(0, 0, smoke.width, smoke.height);
    ctx.globalCompositeOperation = 'lighter';

    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      const angle = flowAngle(p.x + p.seed, p.y - p.seed, tick + p.seed);
      p.vx = p.vx * 0.962 + Math.cos(angle) * p.swirl;
      p.vy = p.vy * 0.962 + Math.sin(angle) * p.swirl - 0.055;
      p.x += p.vx;
      p.y += p.vy;
      p.r += p.grow;
      p.grow *= 0.992;
      p.life -= p.decay;
      if (p.life <= 0) {
        particles.splice(i, 1);
        continue;
      }
      const alpha = Math.pow(p.life, 1.5) * 0.14;
      const px = p.x * SCALE;
      const py = p.y * SCALE;
      const pr = Math.max(p.r * SCALE, 1);
      const g = ctx.createRadialGradient(px, py, pr * 0.1, px, py, pr);
      g.addColorStop(0, `rgba(255,255,255,${alpha})`);
      g.addColorStop(0.55, `rgba(255,255,255,${alpha * 0.5})`);
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(px, py, pr, 0, Math.PI * 2);
      ctx.fill();
    }

    /* ink rings: a hard expanding circle that inverts what it crosses */
    for (let i = rings.length - 1; i >= 0; i--) {
      const ring = rings[i];
      ring.r += ring.v;
      ring.v *= 0.97;
      ring.life -= 0.03;
      if (ring.life <= 0) {
        rings.splice(i, 1);
        continue;
      }
      ctx.strokeStyle = `rgba(255,255,255,${(ring.life * 0.9).toFixed(3)})`;
      ctx.lineWidth = Math.max(ring.life * 5, 0.6);
      ctx.beginPath();
      ctx.arc(ring.x * SCALE, ring.y * SCALE, ring.r * SCALE, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.globalCompositeOperation = 'source-over';
    smokeRaf = particles.length || rings.length ? window.requestAnimationFrame(smokeLoop) : null;
  };

  document.addEventListener(
    'pointermove',
    (event) => {
      mx = event.clientX;
      my = event.clientY;
      if (lastX < 0) {
        lastX = mx;
        lastY = my;
      }
      const overInteractive = Boolean(event.target.closest(INTERACTIVE));
      hoverBoost = overInteractive ? 2.4 : 1.25;
      document.body.classList.add('pfx-cursor-active');
      document.body.classList.toggle('pfx-cursor-hover', overInteractive);
      dot.style.translate = `${mx}px ${my}px`;
      emitPath(lastX, lastY, mx, my);
      lastX = mx;
      lastY = my;
      if (!smokeRaf) smokeLoop();
    },
    { passive: true }
  );

  document.addEventListener('pointerdown', (event) => {
    for (let i = 0; i < 16; i++) {
      spawn(
        event.clientX + (Math.random() - 0.5) * 26,
        event.clientY + (Math.random() - 0.5) * 26,
        (Math.random() - 0.5) * 26,
        -Math.random() * 22
      );
    }
    rings.push({ x: event.clientX, y: event.clientY, r: 4, v: 11, life: 1 });
    if (!smokeRaf) smokeLoop();
  });

  document.addEventListener('pointerleave', () => {
    document.body.classList.remove('pfx-cursor-active');
  });

  /* =================================================================
     11. Magnetic buttons — real springs with overshoot
     ================================================================= */
  const springs = [];
  let springRaf = null;

  const springLoop = () => {
    let live = false;
    for (const s of springs) {
      s.vx += (s.tx - s.x) * 0.16;
      s.vy += (s.ty - s.y) * 0.16;
      s.vx *= 0.76;
      s.vy *= 0.76;
      s.x += s.vx;
      s.y += s.vy;
      const moving = Math.abs(s.vx) + Math.abs(s.vy) > 0.02 || Math.abs(s.tx - s.x) + Math.abs(s.ty - s.y) > 0.05;
      if (moving || s.hover) {
        live = true;
        const scale = s.hover ? 1.04 : 1;
        s.el.style.transform = `translate(${s.x.toFixed(2)}px, ${s.y.toFixed(2)}px) scale(${scale})`;
      } else if (s.el.style.transform) {
        s.el.style.transform = '';
        s.x = s.y = s.vx = s.vy = 0;
      }
    }
    springRaf = live ? window.requestAnimationFrame(springLoop) : null;
  };

  const wakeSprings = () => {
    if (!springRaf) springRaf = window.requestAnimationFrame(springLoop);
  };

  document.querySelectorAll('.button:not(.button-unstyled), [data-magnetic]').forEach((el) => {
    const s = { el, x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0, hover: false };
    springs.push(s);
    el.addEventListener('pointermove', (event) => {
      const rect = el.getBoundingClientRect();
      s.tx = (event.clientX - (rect.left + rect.width / 2)) * 0.24;
      s.ty = (event.clientY - (rect.top + rect.height / 2)) * 0.24 - 2;
      s.hover = true;
      wakeSprings();
    });
    el.addEventListener('pointerleave', () => {
      s.tx = 0;
      s.ty = 0;
      s.hover = false;
      wakeSprings();
    });
  });

  /* =================================================================
     12. Cursor-tracked specular glow + tilt on cards
     ================================================================= */
  const TILT_MAX = 4.5;
  document.querySelectorAll('.product-card, .resource-card, .collection-card').forEach((card) => {
    card.classList.add('pfx-glow');
    let raf = null;
    card.addEventListener('pointermove', (event) => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        const rect = card.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        card.style.setProperty('--pfx-mx', `${((x / rect.width) * 100).toFixed(2)}%`);
        card.style.setProperty('--pfx-my', `${((y / rect.height) * 100).toFixed(2)}%`);
        const rxDeg = ((y / rect.height - 0.5) * -2 * TILT_MAX).toFixed(2);
        const ryDeg = ((x / rect.width - 0.5) * 2 * TILT_MAX).toFixed(2);
        card.style.transform = `perspective(900px) rotateX(${rxDeg}deg) rotateY(${ryDeg}deg) translateY(-6px)`;
      });
    });
    card.addEventListener('pointerleave', () => {
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = null;
      }
      card.style.transform = '';
    });
  });
})();
